import React, { useEffect, useState } from 'react';

export type ToastProps = {
  message: string;
  type?: 'success' | 'error' | 'info';
  duration?: number; // ms
  onClose: () => void;
};

const palette: Record<NonNullable<ToastProps['type']>, { ring: string; bg: string; text: string; gradient: string }> = {
  success: {
    ring: 'ring-green-200/60',
    bg: 'bg-white',
    text: 'text-green-700',
    gradient: 'from-green-500 to-emerald-500',
  },
  error: {
    ring: 'ring-red-200/60',
    bg: 'bg-white',
    text: 'text-red-700',
    gradient: 'from-rose-500 to-red-500',
  },
  info: {
    ring: 'ring-blue-200/60',
    bg: 'bg-white',
    text: 'text-blue-700',
    gradient: 'from-blue-500 to-indigo-500',
  },
};

const icons: Record<NonNullable<ToastProps['type']>, React.ReactNode> = {
  success: (
    <svg className="h-5 w-5 text-green-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414l2.293 2.293 6.543-6.543a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  ),
  error: (
    <svg className="h-5 w-5 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 2l9.5 16.5a2 2 0 01-1.7 3H4.2a2 2 0 01-1.7-3L12 2z" />
      <path d="M12 9v5" strokeLinecap="round" />
      <path d="M12 17h.01" strokeLinecap="round" />
    </svg>
  ),
  info: (
    <svg className="h-5 w-5 text-blue-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M18 10A8 8 0 11.001 10 8 8 0 0118 10zM9 9a1 1 0 112 0v5a1 1 0 11-2 0V9zm1-4a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clipRule="evenodd" />
    </svg>
  ),
};

const Toast: React.FC<ToastProps> = ({ message, type = 'info', duration = 2500, onClose }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Enter animation
    const t = setTimeout(() => setVisible(true), 10);
    // Auto close
    const id = setTimeout(() => setVisible(false), duration);
    // Unmount slightly after exit animation
    const off = setTimeout(onClose, duration + 250);
    return () => {
      clearTimeout(t);
      clearTimeout(id);
      clearTimeout(off);
    };
  }, [duration, onClose]);

  const theme = palette[type];

  return (
    <div
      role="status"
      aria-live="polite"
      className={`pointer-events-auto inline-flex items-center gap-3 ${theme.bg} ${theme.text} rounded-xl px-4 py-3 shadow-lg ring-1 ${theme.ring} transition-all duration-200 ease-out
      ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}
    >
      <span className={`h-6 w-1.5 rounded-full bg-gradient-to-b ${theme.gradient}`} />
      {icons[type]}
      <span className="text-sm font-medium">{message}</span>
      <button
        onClick={() => setVisible(false)}
        className="ml-2 rounded-md border border-black/10 px-2 py-0.5 text-xs opacity-70 hover:opacity-100"
        aria-label="Close notification"
      >
        Close
      </button>
    </div>
  );
};

export default Toast;
